import { processAmendment } from '../../shared/amendmentHelpers';

/**
 * Process manage members amendment (add and/or remove)
 * @param {Object} quotationPayload - The new quotation data
 * @param {Object} policy - Current policy object
 * @param {Array} removedMembers - Members that were removed
 * @param {Array} addedMembers - Members that were added
 * @param {Function} updateStatus - Callback to update loading status
 * @returns {Promise} - Resolves when amendment is complete
 */
export const processRemovalAmendment = async (quotationPayload, policy, removedMembers, addedMembers, updateStatus) => {
  try {
    // Build removed members list
    const removedMembersList = removedMembers.map(m =>
      `* ${m.first_name} ${m.last_name} (${m.relationship})`
    ).join('\n');

    // Build added members list
    const addedMembersList = addedMembers.map(m =>
      `* ${m.first_name} ${m.last_name} (${m.relationship})`
    ).join('\n');

    // Calculate premium details
    const oldPremium = policy.quotation.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const newPremium = quotationPayload.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const premiumDifference = newPremium - oldPremium;
    const changeType = premiumDifference > 0 ? 'Increase' : (premiumDifference < 0 ? 'Decrease' : 'No Change');

    // Build sections for the note
    const hasRemovals = removedMembers.length > 0;
    const hasAdditions = addedMembers.length > 0;

    const removedSection = hasRemovals ? `Removed (${removedMembers.length}):
${removedMembersList}

` : '';

    const addedSection = hasAdditions ? `Added (${addedMembers.length}):
${addedMembersList}

` : '';

    // Build concise policy note
    const noteContent = `MANAGE MEMBERS AMENDMENT

Policy: ${policy.policy_number}

${removedSection}${addedSection}Premium:
Old: BWP ${oldPremium.toFixed(2)}
New: BWP ${newPremium.toFixed(2)}
${changeType}: BWP ${Math.abs(premiumDifference).toFixed(2)}

Total: ${quotationPayload.policy_schedule_entries.length} members`;

    // Use shared amendment processing (handles sanitization and length validation)
    return await processAmendment({
      quotationPayload,
      policy,
      noteContent,
      updateStatus
    });
  } catch (error) {
    console.error('Manage members amendment error:', error);
    throw new Error(
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Failed to process member changes. Please try again.'
    );
  }
};
